import {
	ROAD_AI_POLL_INTERVAL_MS,
	type RoadAiJobHandle,
	type RoadAiJobProgress,
} from "./roadAi.types";
import {
	convertHttpErrorToRunErrorCode,
	convertJobResultToProgress,
} from "./roadAiAdapter";
import { fetchRoadAiJob } from "./roadAiApiClient";

const sleep = (ms: number): Promise<void> =>
	new Promise((resolve) => setTimeout(resolve, ms));

/**
 * 一過性の通信断で失敗を確定しないための、連続通信失敗の上限回数。
 * 3 秒間隔と合わせて十数秒の断（回線切替・デプロイ等）までは継続する。
 * サーバーが返す failed には適用しない（即時確定する）。
 */
export const ROAD_AI_POLL_MAX_CONSECUTIVE_NETWORK_FAILURES = 5;

/**
 * ジョブを 3 秒間隔で照会し、完了（succeeded）または失敗（failed）で解決する。
 *
 * 上限回数・上限時間・中止手段は設けない（AC-31 / AC-41）。終了はサーバーが返す
 * succeeded / failed にのみ従い、client 側では経過時間を数えない。前回の応答が
 * 返るまで次の照会を出さない（照会の重なりを避けるため setInterval を使わない）。
 * 通信失敗（NETWORK_ERROR）だけは一過性とみなし、連続で上限回数に達したときに
 * のみ失敗を確定する。
 */
export const pollRoadAiJob = async (
	siteId: string,
	jobHandle: RoadAiJobHandle,
	onProgress?: (progress: RoadAiJobProgress) => void,
): Promise<RoadAiJobProgress> => {
	let consecutiveNetworkFailures = 0;
	for (;;) {
		const progress = await fetchJobProgress(siteId, jobHandle);
		if (
			progress.status === "failed" &&
			progress.errorCode === "NETWORK_ERROR"
		) {
			consecutiveNetworkFailures += 1;
			if (
				consecutiveNetworkFailures <
				ROAD_AI_POLL_MAX_CONSECUTIVE_NETWORK_FAILURES
			) {
				// 一過性の通信断はポーリング継続で吸収する（onProgress へは流さない）。
				await sleep(ROAD_AI_POLL_INTERVAL_MS);
				continue;
			}
		} else {
			consecutiveNetworkFailures = 0;
		}
		if (onProgress) onProgress(progress);
		if (progress.status !== "pending") {
			return progress;
		}
		await sleep(ROAD_AI_POLL_INTERVAL_MS);
	}
};

/** 1 回の照会結果を進捗表現へ畳む。通信失敗も失敗として扱う。 */
const fetchJobProgress = async (
	siteId: string,
	jobHandle: RoadAiJobHandle,
): Promise<RoadAiJobProgress> => {
	let response: Response;
	try {
		response = await fetchRoadAiJob(siteId, jobHandle);
	} catch (e: unknown) {
		console.error("Failed to poll road AI job", e);
		return { status: "failed", errorCode: "NETWORK_ERROR" };
	}

	const body = await response.json().catch(() => null);
	if (!response.ok) {
		return {
			status: "failed",
			errorCode: convertHttpErrorToRunErrorCode(response.status, body),
		};
	}
	return convertJobResultToProgress(body);
};
